import Router from "next/router";
import { create } from "zustand";
import { getUserOrganizationPermissions } from "@/services/user";
import { getProgramProfile } from "@/services/reports";
import {
  getOrganizationProfile,
  getCurrentEmployeesByLicense,
  getNewHireFormSchemaAndData,
  getNewHireFormCenterTypes,
  getOrganization1099s,
} from "@/services/organization";
import { baseURL, http } from "@/services/http";
import {
  getConfiguration,
  getTrainingOrganizations,
  loadBrightspaceOrgs,
  loadInsightOrgs,
} from "@/services/eventManagement";
import {
  getQueuedReports,
  getOdh_hvPermissionDetails,
  getPrt_grantedPermissionDetails,
} from "@/services/reports/index";
import { abbreviatedTrainingFormats } from "@/stores/eventManagementStore";

export const useReportStore = create((set) => ({
  trainingOrganizations: [],
  selectedTrainingOrganization: null,
  organizations: [],
  organizationProfile: null,
  selectedOrganization: null,
  enrollmentData: [],
  employmentData: [],
  currentEmployees: null,
  formerEmployees: null,
  orgPayments: [],
  orgRepayments: [],
  org1099s: null,
  orgApplications: null,
  paymentInformation: null,
  orgCurrentEmployeesLoading: false,
  orgFormerEmployeesLoading: false,
  currentEmployeesByLicense: null,
  brightspaceAndInsightOrgsLoading: false,
  brightspaceOrgs: [],
  insightOrgs: [],
  isBrightspace: false,
  isInsight: false,
  ppInfo: null,
  orgCurrentEmployeesByLicenseLoading: false,
  employeeTabIndex: 0,
  loading: false,
  applicationsLoading: false,
  organizationLoading: false,
  paymentInfoLoading: false,
  repaymentLoading: false,
  paymentLoading: false,
  ppInfoLoading: false,
  initialOrgLoad: false,
  formDataLoading: false,
  formSchemaLoading: false,
  loading1099s: false,
  error: "",
  successMessage: "",
  newHireOpened: false,
  selectedEmployee: null,
  newHireFormData: null,
  newHireGroupedFields: null,
  newHireFormikMap: null,
  groupedChecklistFields: null,
  newHireFormCenterTypes: [],
  newHireFormSaving: false,
  loadingQueuedReports: false,
  loadingReportPermissions: false,
  queuedReports: null,
  reportPermissions: {
    odh_hv: null,
    special: null,
  },
  setSelectedOrganization: async (organization) => {
    const { organizationLoading } = useReportStore.getState();
    if (organizationLoading) return;
    if (!organization) {
      set((state) => ({
        ...state,
        selectedOrganization: null,
        organizationProfile: null,
      }));
      return;
    }
    try {
      let organizationProfile = await getProgramProfile(organization.id);
      const {
        org_type,
        info,
        employees,
        unverified_employees,
        organization_id_hash,
      } = organizationProfile;
      const enrollmentData = mapEnrollmentData(info);
      const employmentData = mapEmploymentData(employees);
      if (org_type === "qrtp") {
        window.open(
          `${baseURL}reports/index/${organization_id_hash}`,
          "_blank"
        );
      } else {
        if (Router.pathname.includes("/reports")) {
          const paths = Router.asPath.split("/");
          const currentOrgId = paths[2];
          if (currentOrgId !== organization.id) {
            // Substitute the correct organization ID and redirect
            Router.push(`/reports/${organization.id}/org-reports`);
          }
        } else {
          Router.push(`/reports/${organization.id}/org-reports`);
        }
      }
      set((state) => ({
        ...state,
        selectedOrganization: organization,
        organizationProfile,
        enrollmentData,
        employmentData,
        currentEmployees: null,
        formerEmployees: null,
        orgPayments: [],
        orgRepayments: [],
        initialOrgLoad: true,
        paymentInformation: null,
        employeeTabIndex:
          unverified_employees && unverified_employees.length > 0 ? 2 : 0,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch organization profile at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        organizationLoading: false,
      }));
    }
  },
  refreshSelectedOrganization: async () => {
    const { organizationLoading, selectedOrganization } =
      useReportStore.getState();
    if (organizationLoading || !selectedOrganization) return;
    try {
      let organizationProfile = await getOrganizationProfile(
        selectedOrganization.id
      );
      const { info, employees } = organizationProfile;
      const enrollmentData = mapEnrollmentData(info);
      const employmentData = mapEmploymentData(employees);
      set((state) => ({
        ...state,
        organizationProfile,
        enrollmentData,
        employmentData,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch organization profile at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        organizationLoading: false,
      }));
    }
  },
  fetchOrganizations: async (clearCache) => {
    const { organizations, loading } = useReportStore.getState();
    if (loading) return;
    if (organizations.length > 0 && !clearCache) {
      return organizations;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      let organizations = await getUserOrganizationPermissions();
      organizations = Object.values(organizations).map((o) => ({
        id: o.id,
        name: o.license ? `${o.name} - ${o.license}` : o.name,
      }));
      set((state) => ({
        ...state,
        error: "",
        organizations,
      }));
      return organizations;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch organization dashboard permissions at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  fetchCurrentEmployees: async () => {
    const {
      selectedOrganization,
      organizationLoading,
      orgCurrentEmployeesLoading,
      organizationProfile,
    } = useReportStore.getState();
    if (
      organizationLoading ||
      !selectedOrganization ||
      orgCurrentEmployeesLoading
    )
      return;
    try {
      set((state) => ({
        ...state,
        orgCurrentEmployeesLoading: true,
      }));
      let currentEmployees = null;
      if (organizationProfile.org_type === "org") {
        const { employees } = await getOrganizationCurrentEmployees(
          selectedOrganization.id
        );
        currentEmployees = employees;
      } else if (organizationProfile.org_type === "odh") {
        currentEmployees = await getODHCurrentEmployees(
          organizationProfile.info.license_number
        );
        currentEmployees = currentEmployees.map((e) => ({
          ...e,
          hvs_type: (e.hvs && e.hvs.length > 0 && e.hvs[0].name) || null,
          hvs_award_date:
            (e.hvs && e.hvs.length > 0 && e.hvs[0].award_date) || null,
          hvs_expires_date:
            (e.hvs && e.hvs.length > 0 && e.hvs[0].expires_date) || null,
          other_creds_label:
            (e.other_creds && e.other_creds.length > 0 && e.other_creds[0]) ||
            null,
        }));
      } else if (organizationProfile.org_type === "dodd") {
        currentEmployees = await getDODDCurrentEmployees(
          organizationProfile.info.license_number
        );
        currentEmployees = currentEmployees.map((e) => ({
          ...e,
          eiscs_type:
            (e.eiscs && e.eiscs.length > 0 && e.eiscs[0].name) || null,
          eiscs_award_date:
            (e.eiscs && e.eiscs.length > 0 && e.eiscs[0].award_date) || null,
          eiscs_expires_date:
            (e.eiscs && e.eiscs.length > 0 && e.eiscs[0].expires_date) || null,
        }));
      }
      currentEmployees.sort((a, b) => {
        return a.last_name.localeCompare(b.last_name);
      });
      set((state) => ({
        ...state,
        currentEmployees,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch current employees at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        orgCurrentEmployeesLoading: false,
      }));
    }
  },
  fetchCurrentEmployeesByLicense: async (licenseNumber) => {
    const {
      organizationProfile,
      organizationLoading,
      orgCurrentEmployeesByLicenseLoading,
    } = useReportStore.getState();

    if (
      organizationLoading ||
      !organizationProfile ||
      !organizationProfile.info ||
      orgCurrentEmployeesByLicenseLoading
    )
      return;
    try {
      set((state) => ({
        ...state,
        orgCurrentEmployeesByLicenseLoading: true,
      }));
      const currentEmployeesByLicense = await getCurrentEmployeesByLicense(
        organizationProfile.info.license_number
      );
      set((state) => ({
        ...state,
        currentEmployeesByLicense,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch current employees at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        orgCurrentEmployeesByLicenseLoading: false,
      }));
    }
  },
  setError: (error) => {
    set((state) => ({ ...state, error }));
  },
  setSuccessMessage: (message) => {
    set((state) => ({ ...state, successMessage: message }));
  },
  fetchOrganization1099s: async (licenseNumber) => {
    const { loading1099s } = useReportStore.getState();
    if (loading1099s) return;
    set((state) => ({ ...state, loading1099s: true }));
    try {
      let org1099s = await getOrganization1099s(licenseNumber);
      set((state) => ({
        ...state,
        error: "",
        org1099s,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch organization dashboard 1099s at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loading1099s: false,
      }));
    }
  },
  fetchConfiguration: async () => {
    const { configurationLoading } = useReportStore.getState();
    if (configurationLoading) return;
    set((state) => ({
      ...state,
      configurationLoading: true,
    }));
    try {
      const data = await getConfiguration();
      data.training_formats_array = Object.entries(
        data.training_formats_array
      ).map(([key, value]) => {
        return {
          value: key,
          label: value,
          abbreviation: abbreviatedTrainingFormats[key],
        };
      });
      data.author_types = Object.entries(data.author_types).map(
        ([key, value]) => ({ value: Number(key), label: value })
      );
      data.author_types.sort((a, b) => a.label.localeCompare(b.label));

      set((state) => ({
        ...state,
        configuration: data,
      }));
      return data;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Configuration at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        configurationLoading: false,
      }));
    }
  },
  fetchTrainingOrganizations: async () => {
    const { trainingOrgsLoading } = useReportStore.getState();

    if (trainingOrgsLoading) {
      return;
    }
    set({ trainingOrgsLoading: true });
    try {
      const data = await getTrainingOrganizations();
      const sortedData = data.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
      const mappedData = sortedData.map((org) => {
        // We only want to show SDA ID if it's less than 13
        if (org.sda_id && org.sda_id !== "0" && Number(org.sda_id) < 13) {
          org.name = `${org.name} (SDA ${org.sda_id})`;
        }
        return org;
      });
      set({ trainingOrganizations: mappedData });
      return mappedData;
    } catch (error) {
      console.log("Error fetching training orgs: ", error);
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Training org events at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        trainingOrgsLoading: false,
      }));
    }
  },
  setSelectedTrainingOrganization: async (
    trainingOrg,
    trainingOrgIdFromUrl
  ) => {
    const { fetchBrightspaceAndInsightOrgs, fetchPermissionsByTrainingOrg } =
      useReportStore.getState();

    if (!trainingOrg) {
      set({ selectedTrainingOrganization: null });
      return;
    }

    if (Router.pathname.includes("/reports/")) {
      const paths = Router.pathname.split("/");
      const currentPage = paths[3];
      if (trainingOrgIdFromUrl !== trainingOrg.id) {
        // Substitute the correct organization ID and redirect
        Router.push(`/reports/${trainingOrg.id}/reports`);
      }
    } else {
      Router.push(`/reports/${trainingOrg.id}/reports`);
    }
    const { brightspace, insight } = await fetchBrightspaceAndInsightOrgs();
    // Map Data as needed
    set({
      selectedTrainingOrganization: trainingOrg,
      initialTrainingOrgLoad: true,
      isBrightspace:
        brightspace.findIndex((OIN) => trainingOrg.id === OIN) !== -1,
      isInsight: insight.findIndex((OIN) => trainingOrg.id === OIN) !== -1,
    });
    fetchPermissionsByTrainingOrg();
  },
  fetchBrightspaceAndInsightOrgs: async () => {
    const { brightspaceAndInsightOrgsLoading, brightspaceOrgs, insightOrgs } =
      useReportStore.getState();
    // Cache this data and reuse it, it should not change often.
    if (
      brightspaceAndInsightOrgsLoading ||
      brightspaceOrgs.length > 0 ||
      insightOrgs.length > 0
    )
      return { brightspace: brightspaceOrgs, insight: insightOrgs };
    set((state) => ({
      ...state,
      brightspaceAndInsightOrgsLoading: true,
    }));
    try {
      const [brightspaceOrgs, insightOrgs] = await Promise.all([
        loadBrightspaceOrgs(),
        loadInsightOrgs(),
      ]);
      set((state) => ({
        ...state,
        brightspaceOrgs,
        insightOrgs,
      }));
      return { brightspace: brightspaceOrgs, insight: insightOrgs };
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Brightspace and Insight Organizations at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        brightspaceAndInsightOrgsLoading: false,
      }));
    }
  },
  fetchPermissionsByTrainingOrg: async () => {
    const { selectedTrainingOrganization } = useReportStore.getState();

    http
      .get(`/api/v2/organization/${selectedTrainingOrganization.id}/user/me`)
      .then((response) => {
        if (response.status < 200 || response.status >= 300) {
          throw new Error("The response was not OK");
        }
        return response.data;
      })
      .then((data) => {
        set((state) => ({
          ...state,
          permissionsByTrainingOrg: data,
        }));
      })
      .catch((error) => {
        console.error("There was a problem loading the event:", error);
      });
  },
  fetchNewHireFormSchemaAndData: async (userId, licenseNumber) => {
    const { formSchemaLoading, organizationProfile } =
      useOrgDashboardStore.getState();
    if (formSchemaLoading) return;
    try {
      set((state) => ({
        ...state,
        formSchemaLoading: true,
        loading: true,
      }));
      const newHireFormSchemaAndData = await getNewHireFormSchemaAndData(
        organizationProfile.info.center_type,
        userId,
        licenseNumber
      );
      const newHireFormSchema = newHireFormSchemaAndData.form_schema;
      const newHireFormData = newHireFormSchemaAndData.form_data;
      const newHireGroupedFields =
        groupNewHireFieldsBySection(newHireFormSchema);
      const newHireFormikMap = defineNewHireFormikMap(
        newHireFormSchema,
        newHireFormData
      );
      set((state) => ({
        ...state,
        error: "",
        newHireGroupedFields,
        newHireFormikMap,
        newHireFormData,
        formSchemaLoading: false,
        loading: false,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        loading: false,
        error:
          "Unable to fetch new hire form schema at this time. Please try again or contact support.",
      }));
    }
  },
  fetchNewHireFormCenterTypes: async () => {
    try {
      const newHireFormCenterTypes = await getNewHireFormCenterTypes();
      set((state) => ({
        ...state,
        newHireFormCenterTypes,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: "Unable to fetch New Hire Form Checklist allowed center types.",
      }));
    }
  },
  fetchQueuedReports: async (userId) => {
    const { loadingQueuedReports, queuedReports } = useReportStore.getState();
    if (loadingQueuedReports || queuedReports) return queuedReports;
    set((state) => ({
      ...state,
      loadingQueuedReports: true,
    }));
    try {
      const queuedReports = await getQueuedReports(userId);
      set((state) => ({
        ...state,
        queuedReports,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch queued reports at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loadingQueuedReports: false,
      }));
    }
  },
  fetchReportPermissions: async (userId) => {
    const { loadingReportPermissions, reportPermissions } =
      useReportStore.getState();
    // If we already have the permissions, don't fetch them again
    if (
      loadingReportPermissions ||
      typeof reportPermissions?.odh_hv === "boolean" ||
      typeof reportPermissions?.special === "boolean"
    )
      return reportPermissions;
    set((state) => ({
      ...state,
      loadingReportPermissions: true,
    }));
    try {
      const odhPermissionsResponse = await getOdh_hvPermissionDetails(userId);
      const prtPermissionsResponse = await getPrt_grantedPermissionDetails();
      set((state) => ({
        ...state,
        reportPermissions: {
          odh_hv: odhPermissionsResponse,
          special: prtPermissionsResponse,
        },
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch report permissions at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loadingReportPermissions: false,
      }));
    }
  },
}));

const mapEnrollmentData = (info) => {
  const enrollmentData = [];
  enrollmentData.push({
    ageGroup: "Infants",
    fullTime: info.infant_enrollment,
    partTime: info.infant_enrollment_pt,
    capacity: info.infant_capacity,
    total: Number(info.infant_enrollment) + Number(info.infant_enrollment_pt),
  });
  enrollmentData.push({
    ageGroup: "Toddlers",
    fullTime: info.toddler_enrollment,
    partTime: info.toddler_enrollment_pt,
    capacity: info.toddler_capacity,
    total: Number(info.toddler_enrollment) + Number(info.toddler_enrollment_pt),
  });
  enrollmentData.push({
    ageGroup: "Preschool",
    fullTime: info.pre_school_enrollment,
    partTime: info.pre_school_enrollment_pt,
    capacity: info.pre_school_capacity,
    total:
      Number(info.pre_school_enrollment) +
      Number(info.pre_school_enrollment_pt),
  });
  enrollmentData.push({
    ageGroup: "School Age",
    fullTime: info.school_age_enrollment,
    partTime: info.school_age_enrollment_pt,
    capacity: info.school_age_capacity,
    total:
      Number(info.school_age_enrollment) +
      Number(info.school_age_enrollment_pt),
  });
  enrollmentData.push({
    ageGroup: "Total",
    fullTime:
      Number(info.infant_enrollment) +
      Number(info.toddler_enrollment) +
      Number(info.pre_school_enrollment) +
      Number(info.school_age_enrollment),
    partTime:
      Number(info.infant_enrollment_pt) +
      Number(info.toddler_enrollment_pt) +
      Number(info.pre_school_enrollment_pt) +
      Number(info.school_age_enrollment_pt),
    capacity:
      Number(info.infant_capacity) +
      Number(info.toddler_capacity) +
      Number(info.pre_school_capacity) +
      Number(info.school_age_capacity),
    total:
      Number(info.infant_enrollment) +
      Number(info.toddler_enrollment) +
      Number(info.pre_school_enrollment) +
      Number(info.school_age_enrollment) +
      Number(info.infant_enrollment_pt) +
      Number(info.toddler_enrollment_pt) +
      Number(info.pre_school_enrollment_pt) +
      Number(info.school_age_enrollment_pt),
  });

  return enrollmentData;
};

const mapEmploymentData = (employmentRows) => {
  const employmentData = [];
  let total = 0;
  employmentRows.forEach((row) => {
    employmentData.push({
      title: row.job_title,
      count: row.count,
    });
    total += Number(row.count);
  });
  employmentData.push({
    title: "Total",
    count: total,
  });
  return employmentData;
};
